<template>
    <vx-expandable-card data-test="carriers-card">
        <template v-slot:content>
            <div class="text-xl font-extrabold mb-4">Carriers</div>
            <vx-alert
                v-if="rejectedAfterAccept"
                color="danger"
                :closeable="false"
                class="mb-4"
                data-test="carrier-rejected"
            >
                A carrier rejected after Bandwidth accepted: edit, then Resubmit to Bandwidth
            </vx-alert>
            <vx-alert v-if="!rows.length" color="slate" :closeable="false"
                >No carrier has reported on this campaign yet.</vx-alert
            >
            <vx-table v-else class="xl:grid-cols-6" rowBreakpoint="xl">
                <vx-table-row isHeader>
                    <div>Carrier</div>
                    <div>Status</div>
                    <div>Qualify</div>
                    <div>Review</div>
                    <div>Class / TPM</div>
                    <div>Tier / Daily cap</div>
                </vx-table-row>
                <vx-table-row v-for="row in rows" :key="row.mnoId" :data-test="`carrier-${row.mnoId}`">
                    <vx-table-row-title>{{ mnoLabel(row.mnoId, row.terms?.mno ?? null) }}</vx-table-row-title>
                    <vx-table-row-item>
                        <template v-slot:label> Status: </template>
                        <vx-badge
                            v-if="row.status"
                            :color="operationStatusColors[row.status]"
                            size="xs"
                            data-test="operation-status"
                        >
                            {{ operationStatusLabels[row.status] }}
                        </vx-badge>
                        <vx-badge v-else color="slate" size="xs" data-test="operation-status">Not reported</vx-badge>
                    </vx-table-row-item>
                    <vx-table-row-item>
                        <template v-slot:label> Qualify: </template>
                        {{ row.terms ? yesNo(row.terms.qualify) : '—' }}
                    </vx-table-row-item>
                    <vx-table-row-item>
                        <template v-slot:label> Review: </template>
                        {{ row.terms ? yesNo(row.terms.mno_review) : '—' }}
                    </vx-table-row-item>
                    <vx-table-row-item>
                        <template v-slot:label> Class / TPM: </template>
                        {{ row.terms ? carrierClassLabel(row.terms) : '—' }}
                    </vx-table-row-item>
                    <vx-table-row-item>
                        <template v-slot:label> Tier / Daily cap: </template>
                        {{ row.terms ? carrierTierLabel(row.terms) : '—' }}
                    </vx-table-row-item>
                </vx-table-row>
            </vx-table>
        </template>
    </vx-expandable-card>
</template>

<script lang="ts" setup>
import {
    VxAlert,
    VxBadge,
    VxExpandableCard,
    VxTable,
    VxTableRow,
    VxTableRowItem,
    VxTableRowTitle,
} from '@voxie/frontend-components';
import { computed } from 'vue';
import {
    carrierClassLabel,
    carrierTierLabel,
    mnoLabel,
    operationStatusColors,
    operationStatusLabels,
    yesNo,
} from '~/components/kiosk/kiosk-ten-dlc/registration/tenDlcRegistrationLabels';
import type { TcrMnoTerms, TcrOperationStatus, TcrSharingStatus } from '~/services/adminTenDlcRegistrationClient.types';

const props = defineProps<{
    operationStatus: Record<string, TcrOperationStatus>;
    carrierTerms: Record<string, TcrMnoTerms>;
    sharingStatus: TcrSharingStatus | null;
}>();

// MNOs are not required to report, so the rows come from whichever side knows the carrier.
const rows = computed(() =>
    [...new Set([...Object.keys(props.operationStatus), ...Object.keys(props.carrierTerms)])].sort().map((mnoId) => ({
        mnoId,
        status: props.operationStatus[mnoId] ?? null,
        terms: props.carrierTerms[mnoId] ?? null,
    }))
);

const rejectedAfterAccept = computed(
    () => 'ACCEPTED' === props.sharingStatus && Object.values(props.operationStatus).includes('REJECTED')
);
</script>
